/**
 * Les liens de présences pour les animateurs : la fenêtre de l'écran
 * « Présences du centre ».
 *
 * Un lien par centre et par semaine, à ouvrir sur un téléphone, sans compte.
 * Ici : choisir la semaine (son lundi), créer les liens en un clic, les
 * recopier ou les envoyer sur WhatsApp, voir s'ils servent, et les révoquer.
 * La base fait le reste (migration `20261024230000_les_liens_de_presences`).
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertCircle, Check, Copy, Link2, Loader2, MessageCircle, RefreshCw, Trash2, X } from 'lucide-react'
import { etatDesCodesFaux } from '../../../lib/liens-codes'
import {
  adresseDuLien,
  lundiDe,
  lundiPropose,
  messagePourLesAnims,
  semaineEnClair,
} from '../../../lib/presences/lien'
import {
  creerLesLiens,
  lireLesLiens,
  revoquerLeLien,
  type LienDeGestion,
} from '../../../lib/presences/lien.supabase'

const aujourdHui = (): string => {
  const d = new Date()
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

/** Quatre chiffres au hasard : l'écran en propose un, la coordination peut le changer. */
const codeAleatoire = (): string => {
  const n = new Uint32Array(1)
  crypto.getRandomValues(n)
  return String(n[0] % 10000).padStart(4, '0')
}

const ilYA = (iso: string | null): string => {
  if (!iso) return 'jamais'
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000)
  if (min < 1) return 'à l’instant'
  if (min < 60) return `il y a ${min} min`
  const h = Math.round(min / 60)
  if (h < 24) return `il y a ${h} h`
  return `il y a ${Math.round(h / 24)} j`
}

export function LiensPresences({ onClose }: { onClose: () => void }) {
  const [lundi, setLundi] = useState<string>(() => lundiPropose(aujourdHui()))
  const [code, setCode] = useState<string>(() => codeAleatoire())
  const [liens, setLiens] = useState<LienDeGestion[]>([])
  const [chargement, setChargement] = useState(true)
  const [creation, setCreation] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const [copie, setCopie] = useState<string | null>(null)
  const [aRevoquer, setARevoquer] = useState<LienDeGestion | null>(null)

  const origine = typeof window !== 'undefined' ? window.location.origin : ''

  const charger = useCallback(async () => {
    setChargement(true)
    setErreur(null)
    try {
      setLiens(await lireLesLiens(lundi))
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Impossible de lire les liens')
    } finally {
      setChargement(false)
    }
  }, [lundi])

  useEffect(() => {
    void charger()
  }, [charger])

  const creer = async () => {
    setCreation(true)
    setErreur(null)
    try {
      if (code.length !== 4) {
        setErreur('Le code fait quatre chiffres : la fiche santé des enfants est derrière le lien.')
        return
      }
      const n = await creerLesLiens(lundi, code)
      if (n === 0) setErreur('Aucun centre n’a de stage cette semaine-là : aucun lien créé.')
      await charger()
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'La création a échoué')
    } finally {
      setCreation(false)
    }
  }

  const revoquer = async (lien: LienDeGestion) => {
    setARevoquer(null)
    setErreur(null)
    try {
      await revoquerLeLien(lien.id)
      await charger()
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'La révocation a échoué')
    }
  }

  const copier = async (lien: LienDeGestion) => {
    const texte = messagePourLesAnims({
      centre: lien.centre,
      debut: lien.semaineDebut,
      fin: lien.semaineFin,
      adresse: adresseDuLien(origine, lien.jeton),
      code: lien.code,
    })
    try {
      await navigator.clipboard.writeText(texte)
      setCopie(lien.id)
      setTimeout(() => setCopie((c) => (c === lien.id ? null : c)), 2000)
    } catch {
      setErreur('Le presse-papiers est indisponible : copiez l’adresse à la main.')
    }
  }

  const lienWhatsapp = (lien: LienDeGestion) =>
    `https://wa.me/?text=${encodeURIComponent(
      messagePourLesAnims({
        centre: lien.centre,
        debut: lien.semaineDebut,
        fin: lien.semaineFin,
        adresse: adresseDuLien(origine, lien.jeton),
        code: lien.code,
      }),
    )}`

  const dejaCrees = liens.length > 0
  const fin = useMemo(() => {
    const d = new Date(`${lundi}T00:00:00Z`)
    d.setUTCDate(d.getUTCDate() + 4)
    return d.toISOString().slice(0, 10)
  }, [lundi])

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center" onClick={onClose}>
      <div
        className="max-h-[92vh] w-full overflow-y-auto rounded-t-2xl bg-white shadow-xl sm:max-w-2xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="liens-presences-titre"
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 p-5">
          <div>
            <h2 id="liens-presences-titre" className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <Link2 className="h-5 w-5 text-blue-600" />
              Liens pour les animateurs
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              Un lien par centre, valable du lundi au vendredi, protégé par un code à quatre
              chiffres. L’animateur l’ouvre sur son téléphone, tape le code, choisit son groupe
              et pointe les présences ; le nom d’un enfant ouvre sa fiche (parent, départ, santé, image).
              Pas de compte, pas de mot de passe.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100"
            aria-label="Fermer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 p-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <label className="block sm:col-span-2">
              <span className="mb-1 block text-sm font-medium text-gray-700">Semaine (son lundi)</span>
              <input
                type="date"
                value={lundi}
                onChange={(e) => e.target.value && setLundi(lundiDe(e.target.value))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              />
              <span className="mt-1 block text-xs text-gray-500">{semaineEnClair(lundi, fin)}</span>
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Code à 4 chiffres</span>
              <input
                type="text"
                inputMode="numeric"
                maxLength={4}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="4 chiffres"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 tracking-widest focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              />
              <span className="mt-1 block text-xs text-gray-500">Obligatoire. Le même pour tous les centres de la semaine, à donner de vive voix.</span>
            </label>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={() => void creer()}
              disabled={creation || code.length !== 4}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {creation ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
              {dejaCrees ? 'Recréer les liens de la semaine' : 'Créer les liens de la semaine'}
            </button>
            {dejaCrees && (
              <span className="text-xs text-amber-700">
                Recréer désactive les liens actuels : les anims devront recevoir les nouveaux.
              </span>
            )}
          </div>

          {erreur && (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>{erreur}</span>
            </div>
          )}

          <div>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">
                Liens actifs {chargement ? '' : `(${liens.length})`}
              </h3>
              <button
                type="button"
                onClick={() => void charger()}
                className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${chargement ? 'animate-spin' : ''}`} /> Actualiser
              </button>
            </div>

            {chargement ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : liens.length === 0 ? (
              <p className="rounded-lg bg-gray-50 p-4 text-center text-sm text-gray-500">
                Aucun lien pour cette semaine. Créez-les, puis envoyez chaque lien sur le groupe
                WhatsApp de son centre.
              </p>
            ) : (
              <ul className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200">
                {liens.map((lien) => (
                  <li key={lien.id} className="space-y-2 p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate font-medium text-gray-900">{lien.centre}</p>
                        <p className="text-xs text-gray-500">
                          {lien.libelle}
                          {lien.code && <> · code <span className="font-mono">{lien.code}</span></>}
                          {' · '}
                          {lien.pointages} pointage{lien.pointages > 1 ? 's' : ''}, dernier {ilYA(lien.dernierUsageLe)}
                          {etatDesCodesFaux(lien.essaisCode) && <> · {etatDesCodesFaux(lien.essaisCode)}</>}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setARevoquer(lien)}
                        className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-gray-400 hover:bg-red-50 hover:text-red-600"
                        aria-label={`Désactiver le lien de ${lien.centre}`}
                        title="Désactiver ce lien"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                      <code className="min-w-0 flex-1 truncate rounded bg-gray-50 px-2 py-1.5 text-xs text-gray-700">
                        {adresseDuLien(origine, lien.jeton)}
                      </code>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => void copier(lien)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          {copie === lien.id ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                          {copie === lien.id ? 'Copié' : 'Copier'}
                        </button>
                        <a
                          href={lienWhatsapp(lien)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5 text-sm text-white hover:bg-green-700"
                        >
                          <MessageCircle className="h-4 w-4" /> WhatsApp
                        </a>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <p className="text-xs text-gray-500">
            Le message copié contient le lien, le mode d’emploi en deux lignes et le code. Les
            présences pointées par téléphone apparaissent ici, dans l’écran des présences, comme
            celles saisies à la main.
          </p>
        </div>

        {aRevoquer && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={() => setARevoquer(null)}>
            <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-base font-semibold text-gray-900">Désactiver le lien de {aRevoquer.centre} ?</h3>
              <p className="mt-2 text-sm text-gray-600">
                Les téléphones qui l’ont ouvert ne verront plus rien. Les présences déjà pointées restent.
              </p>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setARevoquer(null)}
                  className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
                >
                  Garder
                </button>
                <button
                  type="button"
                  onClick={() => void revoquer(aRevoquer)}
                  className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
                >
                  Désactiver
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
